import { sortBy } from "lodash";
import { CollectionNode, CollectionNodeObject } from "types";
import { getCollectionNestingDepth, getParentIdFromPath } from "../../../../models/CollectionNode";

export interface TreeNode {
    id: string | number;
    parent: string | number;
    text: string;
    droppable?: boolean;
    data?: CollectionNode;
    nestedCollections?: string[];
    collectionChildrenDepth?: number;
}

/**
 * Get all nested collection names and depth for each collection from tree data
 * @param items - All tree nodes
 * @returns Object with maps for collection names and depth
 */
const getNestedCollectionData = (
    items: TreeNode[],
): { namesMap: Map<string, string[]>; depthMap: Map<string, number> } => {
    const namesMap = new Map<string, string[]>();
    const depthMap = new Map<string, number>();

    // Sort by path depth (deepest first) for bottom-up processing
    const sortedByDepth = sortBy(items, (item) => {
        return getCollectionNestingDepth(item.data?.path ?? "") * -1;
    });

    for (const item of sortedByDepth) {
        if (!item.data || item.data.object !== CollectionNodeObject.GROUP_COLLECTION_NODE) {
            continue;
        }

        const collections: string[] = [];
        let maxChildDepth = 0;

        // Find direct children of this collection that are group collections
        const collectionNestedChildren = items.filter(
            (child): child is TreeNode & { data: CollectionNode & { name: string } } => {
                return (
                    child.parent === item.id &&
                    child.data?.object === CollectionNodeObject.GROUP_COLLECTION_NODE &&
                    !!child.data.name
                );
            },
        );

        for (const child of collectionNestedChildren) {
            // Add direct child collection name
            collections.push(child.data.name);

            // Add nested collections from this child (will be available on non-leaf nodes iteration)
            const nestedCollections = namesMap.get(String(child.id)) ?? [];
            collections.push(...nestedCollections);

            // Calculate max depth from children
            const childDepth = depthMap.get(String(child.id)) ?? 0;
            maxChildDepth = Math.max(maxChildDepth, childDepth + 1);
        }

        namesMap.set(String(item.id), collections);
        depthMap.set(String(item.id), maxChildDepth);
    }

    return { namesMap, depthMap };
};

// Build tree nodes from flat allCollections array using path to determine parent
export const buildTreeNodes = (items: CollectionNode[]): TreeNode[] => {
    const sortedItems = sortBy(items, "sequence");

    // Build initial tree nodes
    const treeNodes: TreeNode[] = sortedItems.map((item) => {
        const isGroup = item.object === CollectionNodeObject.GROUP_COLLECTION_NODE;
        const displayName = item.name ?? "Product";

        return {
            id: item.id,
            parent: getParentIdFromPath(item.path ?? ""),
            text: displayName,
            droppable: isGroup,
            data: { ...item, name: displayName },
        };
    });

    // Calculate nested collection names and depth from tree structure
    const { namesMap, depthMap } = getNestedCollectionData(treeNodes);

    // Add nested collections and depth to nodes
    return treeNodes.map((node) => ({
        ...node,
        nestedCollections: namesMap.get(String(node.id)) ?? [],
        collectionChildrenDepth: depthMap.get(String(node.id)) ?? 0,
    }));
};

// Check if a node can be dropped at a target location
export const canDropNode = (
    dragSourceId?: string | number,
    dropTargetId?: string | number,
    dragSourceData?: CollectionNode,
    dropTargetData?: CollectionNode,
): boolean => {
    if (!dragSourceId || !dragSourceData) return false;

    // Prevent dropping a node into itself
    if (dropTargetId === dragSourceId) return false;

    // Check if target is a descendant of the dragged node
    if (dropTargetData?.path) {
        const draggedId = String(dragSourceId);
        // Target's path contains dragged node's ID means it's a descendant
        if (dropTargetData.path.includes(draggedId)) return false;
    }

    return true;
};
